import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Megaphone,
  PencilSimple,
  Plus,
  Package,
  ShoppingCart,
} from '@phosphor-icons/react';
import {
  createProduct,
  formatMoney,
  getStore,
  listCampaigns,
  listOrders,
  listProducts,
  startCampaign,
  updateProduct,
  type CampaignSummary,
  type OrderSummary,
  type ProductSummary,
} from '@/api/shop';
import { mediaURL, publicURL } from '@/api/media';
import { ImageInput } from '@/components/ImageInput';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  TablePagination,
  useTablePaginationState,
} from '@/components/ui/table-pagination';
import {
  SortableHeaderButton,
  useTableSortState,
} from '@/components/ui/table-sort';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

// ShopStore is the per-store editor (mobile-app §8.6). The page header carries
// store name + team + balance so a leader sees the fund context they're
// editing. Three tabs: products (catalog editing), campaigns (member-attributed
// selling drives), orders (recent activity for fulfilment / refund work). The
// campaign+order detail pages — leaderboards, mark-fulfilment forms, refund
// flow — are subsequent surfaces; this page is the entry point.

const CURRENCIES = ['SEK', 'NOK', 'DKK', 'EUR'] as const;
type Currency = (typeof CURRENCIES)[number];

type StoreTab = 'products' | 'campaigns' | 'orders';

export function ShopStore() {
  const { slug = '', storeID = '' } = useParams<{ slug: string; storeID: string }>();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const [tab, setTab] = useState<StoreTab>('products');

  const store = useQuery({
    queryKey: ['shop', 'store', slug, storeID],
    queryFn: () => getStore(token, slug, storeID),
    enabled: !!slug && !!storeID,
  });

  const currency = (store.data?.currency as Currency | undefined) ?? 'SEK';

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <Link
          to={`/orgs/${slug}/shop`}
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" weight="bold" /> {t('shop.sale.back')}
        </Link>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
            {store.data?.name ?? t('shop.team.loading')}
          </h1>
          {store.data && (
            <p className="font-heading text-sm font-semibold text-muted-foreground">
              {store.data.team_name} ·{' '}
              {formatMoney(store.data.balance_minor, store.data.currency || 'SEK')}
            </p>
          )}
        </div>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as StoreTab)} className="flex flex-col gap-4">
        <TabsList aria-label={t('shop.sale.tabsLabel')}>
          <TabsTrigger value="products">
            <Package className="mr-2 h-4 w-4" weight="bold" /> {t('shop.sale.tabs.products')}
          </TabsTrigger>
          <TabsTrigger value="campaigns">
            <Megaphone className="mr-2 h-4 w-4" weight="bold" /> {t('shop.sale.tabs.campaigns')}
          </TabsTrigger>
          <TabsTrigger value="orders">
            <ShoppingCart className="mr-2 h-4 w-4" weight="bold" /> {t('shop.sale.tabs.orders')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="products">
          <ProductsPanel slug={slug} storeID={storeID} token={token} currency={currency} />
        </TabsContent>
        <TabsContent value="campaigns">
          <CampaignsPanel slug={slug} storeID={storeID} token={token} />
        </TabsContent>
        <TabsContent value="orders">
          <OrdersPanel slug={slug} storeID={storeID} token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Sort columns for the products table. Keep in sync with the SortableHeaderButton
// `column` props below — the hook's allowlist mechanism drops anything that
// isn't listed here, so a stale `?products_sort=removed` URL won't crash.
const PRODUCT_SORT_COLUMNS = ['title', 'price', 'status'] as const;
type ProductSortColumn = (typeof PRODUCT_SORT_COLUMNS)[number];

function ProductsPanel({
  slug,
  storeID,
  token,
  currency,
}: {
  slug: string;
  storeID: string;
  token: string | null;
  currency: Currency;
}) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  // `null` = closed; an object means open. `editing.id === ''` is the create
  // case — same form, no pre-filled fields.
  const [editing, setEditing] = useState<ProductSummary | null>(null);
  const products = useQuery({
    queryKey: ['shop', 'products', slug, storeID],
    queryFn: () => listProducts(token, slug, storeID),
    enabled: !!slug && !!storeID,
  });

  // Pagination + sort state lives in the URL so refresh / back / share keep
  // the user where they were. `products_` prefix keeps these params from
  // colliding with the tab's other tables (campaigns, orders) if they ever
  // grow paginated lists of their own.
  const sortState = useTableSortState<ProductSortColumn>({
    columns: PRODUCT_SORT_COLUMNS,
    prefix: 'products',
  });
  const { page, size, setPage, setSize } = useTablePaginationState({
    prefix: 'products',
    defaultSize: 25,
  });

  const rows = useMemo(() => products.data ?? [], [products.data]);

  // Sort client-side: the store-level catalog is small enough that paging the
  // full array is cheaper than a per-sort round-trip. Title falls back to
  // title-asc when no sort is active (matches the server's default ORDER BY).
  const sorted = useMemo(() => {
    const arr = [...rows];
    const col = sortState.sort ?? 'title';
    const factor = sortState.dir === 'desc' ? -1 : 1;
    arr.sort((a, b) => {
      switch (col) {
        case 'price':
          return factor * (a.price_minor - b.price_minor);
        case 'status':
          return factor * a.status.localeCompare(b.status);
        case 'title':
        default:
          return factor * a.title.localeCompare(b.title);
      }
    });
    return arr;
  }, [rows, sortState.sort, sortState.dir]);

  const total = sorted.length;
  const pageRows = useMemo(
    () => sorted.slice((page - 1) * size, page * size),
    [sorted, page, size],
  );

  const onSaved = () => {
    setEditing(null);
    void qc.invalidateQueries({ queryKey: ['shop', 'products', slug, storeID] });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button
          onClick={() =>
            setEditing({
              id: '',
              title: '',
              description: '',
              status: 'active',
              image_object_key: '',
              price_minor: 0,
              currency,
            })
          }
        >
          <Plus className="h-4 w-4" weight="bold" /> {t('shop.products.newProduct')}
        </Button>
      </div>
      {editing && (
        <ProductForm
          slug={slug}
          storeID={storeID}
          token={token}
          defaultCurrency={currency}
          editing={editing}
          onCancel={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
      {products.isLoading ? (
        <TableSkeleton columns={5} />
      ) : total === 0 ? (
        <EmptyState
          icon={<Package weight="duotone" />}
          title={t('shop.products.emptyTitle')}
          body={t('shop.products.emptyBody')}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[4.5rem]">
                    <span className="sr-only">{t('shop.products.col.image')}</span>
                  </TableHead>
                  <TableHead>
                    <SortableHeaderButton
                      column="title"
                      label={t('shop.products.col.title')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortableHeaderButton
                      column="price"
                      label={t('shop.products.col.price')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="w-[6rem]">
                    <SortableHeaderButton
                      column="status"
                      label={t('shop.products.col.status')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="w-[4rem] text-right">
                    <span className="sr-only">{t('common.edit')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((p: ProductSummary) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <ProductThumb objectKey={p.image_object_key} alt={p.title} />
                    </TableCell>
                    <TableCell>
                      <div className="font-heading font-semibold truncate">{p.title}</div>
                      {p.description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {p.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-heading font-semibold tabular-nums">
                      {p.currency
                        ? formatMoney(p.price_minor, p.currency)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground capitalize">
                      {p.status}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('common.edit')}
                        onClick={() => setEditing(p)}
                      >
                        <PencilSimple className="h-4 w-4" weight="bold" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            total={total}
            page={page}
            size={size}
            onPageChange={setPage}
            onSizeChange={setSize}
          />
        </div>
      )}
    </div>
  );
}

// ProductThumb renders a small square preview for the table. Empty state
// shows a package glyph so the column doesn't collapse to a blank cell.
function ProductThumb({ objectKey, alt }: { objectKey: string; alt: string }) {
  if (!objectKey) {
    return (
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-foreground/40">
        <Package className="h-5 w-5" weight="bold" />
      </div>
    );
  }
  // The resize worker may not have materialised a small variant yet (the row
  // could be newly uploaded); mediaURL falls back to the original, so the
  // table still renders something meaningful. Public-URL path matches the one
  // used everywhere else in the SPA.
  const url = mediaURL(
    {
      object_key: objectKey,
      variants: {},
      // The rest of MediaAsset isn't needed by mediaURL — pass empty values to
      // keep the shape happy without an extra round-trip to the media table.
      id: '',
      kind: 'image',
      content_type: '',
      byte_size: 0,
      status: 'ready',
      uploaded_by: '',
      alt: '',
      caption: '',
      created_at: '',
    },
    'small',
  ) || publicURL(objectKey);
  return (
    <img
      src={url}
      alt={alt}
      className="h-12 w-12 rounded-lg object-cover"
      loading="lazy"
    />
  );
}

function ProductForm({
  slug,
  storeID,
  token,
  defaultCurrency,
  editing,
  onCancel,
  onSaved,
}: {
  slug: string;
  storeID: string;
  token: string | null;
  defaultCurrency: Currency;
  editing: ProductSummary;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('common');
  const isEdit = !!editing.id;
  const [title, setTitle] = useState(editing.title);
  const [description, setDescription] = useState(editing.description);
  // Empty string for a 0-price create so the user sees a blank field; for edit
  // we render the stored value with comma-as-decimal-separator to match the sv
  // typing convention (the parser accepts both).
  const [price, setPrice] = useState(
    isEdit ? (editing.price_minor / 100).toFixed(2) : '',
  );
  const [c, setC] = useState<Currency>(
    (editing.currency as Currency) || defaultCurrency,
  );
  const [imageObjectKey, setImageObjectKey] = useState(editing.image_object_key);

  // If the parent switches `editing` to a different product (rare — only when
  // a user clicks Edit on another row while the form is open), refresh local
  // state. Without this the form would still show the previous product.
  useEffect(() => {
    setTitle(editing.title);
    setDescription(editing.description);
    setPrice(editing.id ? (editing.price_minor / 100).toFixed(2) : '');
    setC((editing.currency as Currency) || defaultCurrency);
    setImageObjectKey(editing.image_object_key);
  }, [editing, defaultCurrency]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title,
        description,
        currency: c,
        price_minor: Math.round(parseFloat(price.replace(',', '.') || '0') * 100),
        image_object_key: imageObjectKey,
      };
      return isEdit
        ? updateProduct(token, slug, editing.id, body).then(() => ({ id: editing.id }))
        : createProduct(token, slug, storeID, body);
    },
    onSuccess: () => onSaved(),
  });

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <h3 className="font-heading text-lg font-bold mb-3">
        {isEdit ? t('shop.products.editProduct') : t('shop.products.newProduct')}
      </h3>
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.products.title')}</span>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.products.price')}</span>
          <Input
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            placeholder="0.00"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.products.currency')}</span>
          <Select value={c} onValueChange={(v) => setC(v as Currency)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((cur) => (
                <SelectItem key={cur} value={cur}>
                  {cur}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="sm:col-span-3 flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.products.description')}</span>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <div className="sm:col-span-3 flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.products.image')}</span>
          <div className="product-form__image">
            <ImageInput
              value={imageObjectKey}
              onChange={setImageObjectKey}
              label={t('shop.products.chooseImage')}
            />
          </div>
        </div>
      </div>
      {save.error instanceof Error && (
        <p className="mt-2 text-sm text-destructive">{save.error.message}</p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={save.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => save.mutate()}
          disabled={!title.trim() || !price.trim() || save.isPending}
        >
          {save.isPending
            ? t('common.loading')
            : isEdit
              ? t('common.save')
              : t('common.create')}
        </Button>
      </div>
    </div>
  );
}

function CampaignsPanel({
  slug,
  storeID,
  token,
}: {
  slug: string;
  storeID: string;
  token: string | null;
}) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const campaigns = useQuery({
    queryKey: ['shop', 'campaigns', slug, storeID],
    queryFn: () => listCampaigns(token, slug, storeID),
    enabled: !!slug && !!storeID,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" weight="bold" /> {t('shop.campaigns.newCampaign')}
        </Button>
      </div>
      {creating && (
        <CreateCampaignForm
          slug={slug}
          storeID={storeID}
          token={token}
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ['shop', 'campaigns', slug, storeID] });
          }}
        />
      )}
      {campaigns.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (campaigns.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<Megaphone weight="duotone" />}
          title={t('shop.campaigns.emptyTitle')}
          body={t('shop.campaigns.emptyBody')}
        />
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-card shadow-sm">
          {(campaigns.data ?? []).map((c: CampaignSummary) => (
            <li key={c.id} className="flex items-center gap-3 px-4 py-3">
              <Megaphone className="h-5 w-5 text-foreground/60" weight="bold" />
              <div className="flex-1 min-w-0">
                <p className="font-heading text-base font-semibold truncate">{c.name}</p>
                <p className="text-sm text-muted-foreground truncate">
                  {t('shop.campaigns.fundShare', {
                    pct: (c.team_fund_share_bps / 100).toFixed(0),
                  })}
                  {' · '}
                  {t('shop.campaigns.sellerCount', { count: c.seller_count })}
                </p>
              </div>
              <p className="text-xs text-muted-foreground capitalize">{c.status}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateCampaignForm({
  slug,
  storeID,
  token,
  onCancel,
  onCreated,
}: {
  slug: string;
  storeID: string;
  token: string | null;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [pct, setPct] = useState('20');
  const create = useMutation({
    mutationFn: () =>
      startCampaign(token, slug, storeID, {
        name,
        team_fund_share_bps: Math.round(parseFloat(pct) * 100),
      }),
    onSuccess: () => onCreated(),
  });

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <h3 className="font-heading text-lg font-bold mb-3">{t('shop.campaigns.newCampaign')}</h3>
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.campaigns.name')}</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shop.campaigns.fundSharePct')}</span>
          <Input
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            inputMode="decimal"
            placeholder="20"
          />
          <span className="text-xs text-muted-foreground">
            {t('shop.campaigns.fundShareHint')}
          </span>
        </label>
      </div>
      {create.error instanceof Error && (
        <p className="mt-2 text-sm text-destructive">{create.error.message}</p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={create.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => create.mutate()}
          disabled={!name.trim() || create.isPending}
        >
          {create.isPending ? t('common.loading') : t('shop.campaigns.startCta')}
        </Button>
      </div>
    </div>
  );
}

function OrdersPanel({
  slug,
  storeID,
  token,
}: {
  slug: string;
  storeID: string;
  token: string | null;
}) {
  const { t } = useTranslation('common');
  const orders = useQuery({
    queryKey: ['shop', 'orders', slug, storeID],
    queryFn: () => listOrders(token, slug, storeID, 200),
    enabled: !!slug && !!storeID,
  });

  if (orders.isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }
  const rows = orders.data ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<ShoppingCart weight="duotone" />}
        title={t('shop.orders.emptyTitle')}
        body={t('shop.orders.emptyBody')}
      />
    );
  }

  return (
    <ul className="divide-y divide-border rounded-2xl border border-border bg-card shadow-sm">
      {rows.map((o: OrderSummary) => (
        <li key={o.id} className="flex items-center gap-3 px-4 py-3">
          <ShoppingCart className="h-5 w-5 text-foreground/60" weight="bold" />
          <div className="flex-1 min-w-0">
            <p className="font-heading text-base font-semibold truncate">
              {o.buyer_name || t('shop.orders.anonymousBuyer')}
            </p>
            <p className="text-sm text-muted-foreground truncate">
              {new Date(o.created_at).toLocaleString()}
              {o.payment_method && ` · ${o.payment_method}`}
            </p>
          </div>
          <div className="text-right">
            <p className="font-heading text-sm font-semibold">
              {formatMoney(o.total_minor, o.currency)}
            </p>
            <p className="text-xs text-muted-foreground capitalize">{o.status}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}
